'use strict';

/*
 * The auth client can be used to trigger a sign in process
 */
function M42AcsSdkAuthClient(providerConfiguration, $window, mxAuthLocation, mxAuthLocationTracker, $timeout, $injector) {
	var self = this,
		finishPendingAuthorizationWorkflowDefaults = {},
		startAuthorizeWorkflowDefaults = {};

	var redirectUriStorageItemKey = 'mx.auth.redirect.uri';
	var codeVerifierStorageItemKey = 'mx.auth.code_verifier';

	/*
	 * This method kicks off the authorization workflow which ends up with the situation that
	 * the system is leaving the page.
	 *
	 * The options parameter allows to add additionla url parameters to the STS. It's possible to
	 * override the standard redirect_uri parameter but no other default OAuth 2 parameters with this
	 * options field.
	 */
	self.startAuthorizeWorkflow = function (options) {

		options = angular.extend({
			redirect_uri: mxAuthLocationTracker.getTargetLocation()
		}, startAuthorizeWorkflowDefaults, options);

		// When the redirect uri is fixed to a specific value because the used STS does not support deep
		// linking we cache the original target and use the fixed redirect uri.
		if (providerConfiguration.redirect_uri) {
			var authCode = getAuthorizationCodeFromUrl();
			if (!authCode && (!localStorage.getItem(redirectUriStorageItemKey) || options.redirect_uri !== providerConfiguration.redirect_uri)) {
				localStorage.setItem(redirectUriStorageItemKey, options.redirect_uri);
			}
			options.redirect_uri = providerConfiguration.redirect_uri;
		}

		// generate the url
		var responseType = 'token';
		var authLocation = providerConfiguration.endpoints.authorize +
			'?client_id=' + providerConfiguration.clientid +
			'&scope=' + providerConfiguration.scope;

		if (options.allowAuthWithPkce) {
			localStorage.setItem(codeVerifierStorageItemKey, options.allowAuthWithPkce.code_verifier);

			responseType = 'code';
			authLocation += '&code_challenge=' + options.allowAuthWithPkce.code_challenge + '&code_challenge_method=' + options.allowAuthWithPkce.code_challenge_method;
		}

		authLocation += '&response_type=' + responseType;

		Object.keys(options)
			// Make sure to not override one of the default OAuth 2 parameters.
			.filter(function (parameterKey) {
				return ['client_id', 'scope', 'response_type', 'allowAuthWithPkce'].indexOf(parameterKey.toLowerCase()) === -1;
			})
			// Add optional paramters.
			.forEach(function (parameterKey) {
				/*
				 Check if the redirect URI is set manually  in the provider Configuration
				 If so, add this redirect uri to the Auth-URL instead of the normal one
				 */
				authLocation += '&' + parameterKey + '=' + encodeURIComponent(options[parameterKey]);
			});

		// leave this page
		$window.location = authLocation;
	};

	function getAuthorizationCodeFromUrl() {
		var searchParams = new URLSearchParams(mxAuthLocation.search());
		return searchParams.get('code');
	}

	function getAuthorizationStateFromUrl() {
		var searchParams = new URLSearchParams(mxAuthLocation.search());
		return searchParams.get('state');
	}

	function getAccessTokenFromHash() {
		var searchParams = new URLSearchParams(mxAuthLocation.hash());
		return searchParams.get('access_token');
	}

	function request(method, url, params, config) {
		return new Promise(function (resolve, reject) {
			var xhr = new XMLHttpRequest();
			xhr.open(method, url, true);
			xhr.responseType = 'json';
			if (config) {
				config(xhr);
			}

			xhr.onload = function () {
				var status = parseInt(xhr.status, 10);
				if (status === 200) {
					var response = xhr.response;
					try {
						response = typeof response === 'string' ? JSON.parse(response) : response;
						resolve(response);
					} catch (e) {
						console.log('Error in handling response: ', response, e);
						reject(xhr);
					}
				} else {
					reject(xhr);
				}
			};

			xhr.ontimeout = function (err) {
				reject(err);
			};
			xhr.onerror = function (err) {
				reject(err);
			};

			xhr.send(params || null);
		});
	}

	/*
	 * This method exchanges the authorization code for an access token
	 * after success authentication with Proof Key for Code Exchange (PKCE)
	 *
	 */
	self.exchangeAuthorizationCodeForAccessToken = function () {

		var $q = $injector.get('$q');
		var defer = $q.defer();

		var code_verifier = localStorage.getItem(codeVerifierStorageItemKey);
		if (!code_verifier) {
			defer.reject('Code verifier required');
		}

		var authCode = getAuthorizationCodeFromUrl();
		if (!authCode) {
			defer.reject('Authorization Code required');
		}

		var authState = getAuthorizationStateFromUrl();
		if (!authState) {
			defer.reject('State required');
		}

		var authTokenParams = 'grant_type=authorization_code' +
			'&code=' + authCode +
			'&code_verifier=' + code_verifier +
			'&state=' + authState +
			'&client_id=' + providerConfiguration.clientid +
			'&scope=' + providerConfiguration.scope +
			'&redirect_uri=' + encodeURIComponent(providerConfiguration.redirect_uri);

		request('POST',
			providerConfiguration.endpoints.token,
			authTokenParams,
			function (xhr) {
				xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
			})
			.then(function (response) {
				defer.resolve(response);
			})
			.catch(function (err) {
				var errorMessage = 'Error on exchanging the authorization code for an access token';
				var response = err.response;
				if (typeof response === 'object' && response.error) {
					errorMessage = response.error;
				}
				defer.reject(errorMessage);
			});

		return defer.promise;
	};

	function getSymbolPositionOrLength(url, symbol) {
		const symbolPosition = url.indexOf(symbol);
		return symbolPosition === -1 ? url.length : symbolPosition;
	}

	/*
	 * This page is finishing the authorization workflow which ends up with
	 * an extracted token from the URI
	 *
	 */
	self.finishPendingAuthorizeWorkflow = function (options) {
		options = angular.extend({}, finishPendingAuthorizationWorkflowDefaults, options);

		/*
		Check if we come back from a successful authorization (with token)
		and have an additional Url-Path stored in the local storage
		If so, insert the additional path between the (if configured) redirect Uri and access token
		go to this new location
		 */
		var cachedRedirectUri = localStorage.getItem(redirectUriStorageItemKey);
		var currentURL = window.location.href.toString();
		const currentUrlWithoutHash = currentURL.substring(0, getSymbolPositionOrLength(currentURL, '#'));

		if (!currentURL.startsWith(providerConfiguration.redirect_uri)) {
			currentURL = providerConfiguration.redirect_uri;
		}

		// If current URL is specified (not the same as default)
		// then it has higher priority than stored in localStorage (which was saved on LogOut for restoring last location)
		// because user uses this URL explicitly in address bar.
		if (cachedRedirectUri && currentUrlWithoutHash !== providerConfiguration.redirect_uri) {
			localStorage.removeItem(redirectUriStorageItemKey);
			cachedRedirectUri = null;
		}

		if (cachedRedirectUri) {
			var redirectUriEndIndex = providerConfiguration.redirect_uri.length;
			var params = currentURL.slice(redirectUriEndIndex);

			// For the case when '/' in the end of url does matter.
			// It's need to redirect to exactly the same `currentURL` with or without `/` in the end.
			if (params.indexOf('/?') === 0) {
				params = params.replace('/?', '?');
			}

			// for the case if `cachedRedirectUri` has its own GET params
			if (cachedRedirectUri.indexOf('?') !== -1) {
				// Check if `params` are GET-params or not (like: "/").
				// If not then set to "" not to break GET-params of `cachedRedirectUri`.
				params = params.indexOf('?') === 0 ? params.replace('?', '&') : '';
			}

			var newUrl = [cachedRedirectUri, params].join('');
			localStorage.removeItem(redirectUriStorageItemKey);

			// redirect away
			window.location = newUrl;

			// return nothing but we are on our way to visit the new url
			$timeout(function () {
			}, 60 * 60);
		}

		if (!self.areReturningFromExternalAuthorization()) {
			throw(new Error('M42ACSSdkNg: No authorization request is pending, aborting'));
		}

		var accessToken = getAccessTokenFromHash();

		// remove hash from uri without reload
		mxAuthLocation.skipReload()
			.hash('')
			.replace();

		if (window.location.href.indexOf('#') > -1 && typeof window.history.replaceState === 'function') {
			history.replaceState({}, '', window.location.href.substr(0, window.location.href.indexOf('#')));
		}

		// return the token
		return accessToken;
	};

	/*
	 * This function returns true when we are currently returning from an external authorization.
	 * This is indicated when ever we have an access_token in the URI
	 */
	self.areReturningFromExternalAuthorization = function () {
		var accessToken = getAccessTokenFromHash();
		return accessToken !== null && accessToken !== undefined;
	};

	/*
	 * This function returns true when we are currently returning from an authorization with PKCE.
	 * This is indicated when ever we have an code in the URI as well as is not ACS auth
	 */
	self.areReturningFromExternalAuthorizationWithPKCE = function () {
		return !Boolean(providerConfiguration.auth_with_implicit) && mxAuthLocation.url().indexOf('externalAuth') === -1 && !!getAuthorizationCodeFromUrl();
	};

	/*
	 * This function starts the logout workflow whcih means the following. If the configuration contains
	 * a destroy endpoint the browser will be redirected to them. For this the system generates a
	 * query parameter "redirect_uri" based on the current location in the app.
	 *
	 * @param redirectUri - (Optional) When specified this will be used as a redirect url after signing
	 *                      out. When not specified it will default to the current url.
	 * @param storeCurrentUri - (Optional) Store current Uri to be able to restore it after reLogin
	 */
	self.startLogoutWorkflow = function (redirectUri, storeCurrentUri) {

		// Save current URL to be able to restore it on next reLogin
		var authCode = getAuthorizationCodeFromUrl();
		if (!authCode && storeCurrentUri) {
			localStorage.setItem(redirectUriStorageItemKey, $window.location.origin + $window.location.pathname + $window.location.search);
		}

		// check if we need to do anything
		if (!providerConfiguration.endpoints.signout) {
			return;
		}

		var signoutIncludesRedirectUri = providerConfiguration.endpoints.signout.indexOf('redirect_uri=') >= 0;

		// build the redirect_uri parameter
		if (!redirectUri && !signoutIncludesRedirectUri) {
			redirectUri = mxAuthLocationTracker.getTargetLocation();
		}

		if (redirectUri) {
			redirectUri = (providerConfiguration.endpoints.signout.indexOf('?') === -1 ? '?' : '&') +
				'redirect_uri=' + encodeURIComponent(redirectUri);
		} else {
			redirectUri = '';
		}

		// leave the page
		$window.location = providerConfiguration.endpoints.signout + redirectUri;
	};

	/*
	 * This function allows a delayed configuration of the whole authentication framework. It should be
	 * used in the run function when the config function via provider is somehow not an option. This situation
	 * happens in the Pandora shell.
	 */
	self.configure = function (configuration) {
		angular.extend(providerConfiguration, configuration);
	};
}

angular.module('mx.auth').provider('mxAuthClient', function () {
	var self = this,
		providerConfigurationDefaults = {endpoints: {authorize: null, signout: null}, redirect_uri: null};

	self.providerConfiguration = {};

	// Instantiate the service.
	self.$get = ['$window', 'mxAuthLocation', 'mxAuthLocationTracker', '$timeout', '$injector', function ($window, mxAuthLocation, mxAuthLocationTracker, $timeout, $injector) {
		return new M42AcsSdkAuthClient(self.providerConfiguration, $window, mxAuthLocation, mxAuthLocationTracker, $timeout, $injector);
	}];

	// Setup the authorization client.
	self.setup = function (configuration) {
		self.providerConfiguration = angular.extend({}, providerConfigurationDefaults, configuration);
	};
});

// Make sure initial logic is executed.
angular.module('mx.auth').run(['mxAuthLocation', 'mxAuthLocationTracker', angular.noop]);
